import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Table, Button, message, Select, Tabs, Modal } from "antd";
import { Chart } from "@antv/g2";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import firebase from "firebase/compat/app";
import moment from "moment";
import _ from "lodash";

const { TabPane } = Tabs;

// Constants
const MESSAGE_TYPES = {
  recommande: "Envois recommandés",
  simple: "Envois simples",
  electronique: "Electroniques",
};

const CHART_MESSAGE_TYPES = {
  recommande: "Recommandé",
  simple: "Simple",
  electronique: "Électronique",
};

const MONTHS = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

const EXPORT_FIELDS = [
  "Type d'envoi",
  "Date d'envoi",
  "Type de destinataire",
  "Nom du destinataire",
  "Prénom du destinataire",
  "Email du destinataire",
  "Statut de l'envoi",
  "Date de dernier status",
];

// Year options
const currentYear = moment().year();
const yearOptions = Array.from({ length: currentYear - 2019 }, (_, i) => 2020 + i);

// Utility functions
const sorterFn = (property) => (a, b) =>
  `${a[property]}`.toLowerCase().localeCompare(`${b[property]}`.toLowerCase());

const sorterNumFn = (property) => (a, b) => +a[property] - +b[property];

// In-memory cache
class DataCache {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.ttl = 5 * 60 * 1000; // 5 minutes
  }

  set(key, value) {
    this.cache.set(key, value);
    this.timestamps.set(key, Date.now());
  }

  get(key) {
    const timestamp = this.timestamps.get(key);
    if (!timestamp || Date.now() - timestamp > this.ttl) {
      this.cache.delete(key);
      this.timestamps.delete(key);
      return null;
    }
    return this.cache.get(key);
  }

  has(key) {
    return this.get(key) !== null;
  }

  clear() {
    this.cache.clear();
    this.timestamps.clear();
  }
}

const cache = new DataCache();

export default function StatsComponent() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loadingTable, setLoadingTable] = useState(false);
  const [stackedBarData, setStackedBarData] = useState([]);
  const [tableStatsData, setTableStatsData] = useState([]);
  const [stackBarPlot, setStackBarPlot] = useState(null);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(moment().month());
  const [statsType, setStatsType] = useState("global");
  const [selectedAmountType, setSelectedAmountType] = useState("send");
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedFields, setSelectedFields] = useState(EXPORT_FIELDS);
  const [exportedYears, setExportedYears] = useState([currentYear]);

  const dispatch = useDispatch();
  const db = useRef(firebase.firestore()).current;
  const getUserStats = useRef(
    firebase.app().functions("europe-west1").httpsCallable("getUserStatsCSV")
  ).current;
  const getMonthlyPrintingStats = useRef(
    firebase.app().functions("europe-west1").httpsCallable("getMonthlyPrintingStatsCSV")
  ).current;
  const [exportPrintingModalVisible, setExportPrintingModalVisible] = useState(false);
  const [exportPrintingYear, setExportPrintingYear] = useState(currentYear);
  const [exportPrintingMonth, setExportPrintingMonth] = useState(moment().month() + 1);

  // Abort controller for canceling ongoing requests
  const abortControllerRef = useRef(null);

  // Memoized columns
  const globalColumns = useMemo(
    () => [
      {
        title: "Compte",
        dataIndex: "company",
        key: "company",
        sorter: sorterFn("company"),
        render: (company, record) => (
          <Button
            style={{ color: "#000", fontWeight: "bold" }}
            type="link"
            onClick={() => {
              setStatsType("individual");
              setSelectedCompany(companies.find((c) => c.id === record.key));
            }}
          >
            {company}
          </Button>
        ),
      },
      {
        title: "Type",
        children: [
          {
            title: "Electroniques",
            dataIndex: "electronique",
            key: "electronique",
            sorter: sorterNumFn("electronique"),
          },
          {
            title: "Envois simples",
            dataIndex: "simple",
            key: "simple",
            sorter: sorterNumFn("simple"),
          },
          {
            title: "Envois recommandés",
            dataIndex: "recommande",
            key: "recommande",
            sorter: sorterNumFn("recommande"),
          },
        ],
      },
      {
        title: "Total",
        dataIndex: "total",
        key: "total",
        sorter: sorterNumFn("total"),
        defaultSortOrder: "descend",
      },
    ],
    [companies]
  );

  const individualColumns = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
      },
      {
        title: "Type",
        children: [
          {
            title: "Electroniques",
            dataIndex: "electronique",
            key: "electronique",
          },
          {
            title: "Envois simples",
            dataIndex: "simple",
            key: "simple",
          },
          {
            title: "Envois recommandés",
            dataIndex: "recommande",
            key: "recommande",
          },
        ],
      },
      {
        title: "Total",
        dataIndex: "total",
        key: "total",
      },
    ],
    []
  );

  // Sorted companies memoized
  const sortedCompanies = useMemo(() => _.sortBy(companies, "name"), [companies]);

  // Load companies once on mount
  useEffect(() => {
    const cacheKey = "companies";

    if (cache.has(cacheKey)) {
      setCompanies(cache.get(cacheKey));
      return;
    }

    db.collection("companies")
      .get()
      .then((companiesSnapshot) => {
        const companiesList = companiesSnapshot.docs.map((snap) => ({
          ...snap.data(),
          ref: snap.ref,
          key: snap.id,
          id: snap.id,
        }));

        setCompanies(companiesList);
        cache.set(cacheKey, companiesList);
      })
      .catch((error) => {
        console.error("Error loading companies:", error);
        message.error("Erreur lors du chargement des comptes");
      });
  }, [db]);

  // Helper functions
  const initializeYearData = useCallback(() => {
    const yearData = {};
    for (let i = 0; i < 12; i++) {
      yearData[i] = {};
    }
    return yearData;
  }, []);

  const updateStats = useCallback((statsObj, type, amount) => {
    statsObj[type] = (statsObj[type] || 0) + amount;
    statsObj.total = (statsObj.total || 0) + amount;
  }, []);

  const generateDateTitle = useCallback(
    (monthIndex) => {
      return `${_.capitalize(moment(monthIndex + 1, "MM").format("MMMM"))} ${selectedYear}`;
    },
    [selectedYear]
  );

  const generateGlobalReports = useCallback(
    (messageStats, total, month) => {
      const chartData = [];
      const tableData = [];

      // Generate chart data for totals
      for (let i = 11; i >= 0; i--) {
        const dateTitle = generateDateTitle(i);
        chartData.push({
          Date: dateTitle,
          Type: "Total",
          Total: total[i]?.total || 0,
        });
      }

      // Generate chart data by type
      for (const [type, label] of Object.entries(CHART_MESSAGE_TYPES)) {
        for (let i = 11; i >= 0; i--) {
          const dateTitle = generateDateTitle(i);
          chartData.push({
            Date: dateTitle,
            Type: label,
            Nb: total[i]?.[type] || 0,
          });
        }
      }

      // Generate table data - use the month parameter
      messageStats.forEach((stats, companyId) => {
        const monthStats = stats[selectedYear]?.[month] || {};
        tableData.push({
          key: companyId,
          company: stats.name,
          recommande: monthStats.recommande || 0,
          simple: monthStats.simple || 0,
          electronique: monthStats.electronique || 0,
          total: monthStats.total || 0,
        });
      });

      return {
        chartData: chartData.reverse(),
        tableData,
      };
    },
    [selectedYear, generateDateTitle]
  );

  const generateCompanyReports = useCallback(
    (messageStats) => {
      const chartData = [];
      const tableData = [];

      // Generate chart data
      for (let i = 11; i >= 0; i--) {
        const dateTitle = generateDateTitle(i);
        chartData.push({
          Date: dateTitle,
          Type: "Total",
          Total: messageStats[selectedYear]?.[i]?.total || 0,
        });
      }

      for (const [type, label] of Object.entries(CHART_MESSAGE_TYPES)) {
        for (let i = 11; i >= 0; i--) {
          const dateTitle = generateDateTitle(i);
          chartData.push({
            Date: dateTitle,
            Type: label,
            Nb: messageStats[selectedYear]?.[i]?.[type] || 0,
          });
        }
      }

      // Generate table data
      for (let i = 11; i >= 0; i--) {
        const dateTitle = generateDateTitle(i);
        const monthStats = messageStats[selectedYear]?.[i] || {};
        tableData.push({
          key: i,
          date: dateTitle,
          recommande: monthStats.recommande || 0,
          simple: monthStats.simple || 0,
          electronique: monthStats.electronique || 0,
          total: monthStats.total || 0,
        });
      }

      return {
        chartData: chartData.reverse(),
        tableData: tableData.reverse(),
      };
    },
    [selectedYear, generateDateTitle]
  );

  const fetchGlobalStats = useCallback(async () => {
    const cacheKey = `global-${selectedYear}-${selectedAmountType}`;

    // Check cache first
    if (cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      // Regenerate table data with current month
      const { chartData, tableData } = generateGlobalReports(
        cachedData.messageStats,
        cachedData.total,
        selectedMonth
      );
      setStackedBarData(chartData);
      setTableStatsData(tableData);
      setLoadingTable(false);
      return;
    }

    const yearStart = new Date(`${selectedYear}-01-01`);
    const yearEnd = new Date(`${selectedYear}-12-31`);

    try {
      // Fetch messages and recipients in parallel
      const [msgSnapshot, recipientsSnapshot] = await Promise.all([
        selectedAmountType === "pages"
          ? db
              .collectionGroup("messages")
              .where("created_at", ">", yearStart)
              .where("created_at", "<", yearEnd)
              .get()
          : Promise.resolve({ docs: [] }),
        db
          .collectionGroup("recipients")
          .where("created_at", ">", yearStart)
          .where("created_at", "<", yearEnd)
          .get(),
      ]);

      // Build message cache only if needed
      const msgCache = new Map();
      if (selectedAmountType === "pages") {
        msgSnapshot.forEach((doc) => {
          msgCache.set(doc.id, {
            pages: doc.data().pages || 1,
            id: doc.id,
          });
        });
      }

      // Use cached companies
      const companiesMap = new Map(companies.map((c) => [c.id, c.name]));

      // Process recipients
      const messageStats = new Map();
      const total = initializeYearData();

      recipientsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (!data.created_at || !data.company?.id) return;

        const date = moment(data.created_at.toDate());
        const month = date.month();
        const year = date.year();

        if (year !== selectedYear) return;

        const companyId = data.company.id;
        const companyName = companiesMap.get(companyId);

        if (!messageStats.has(companyId)) {
          messageStats.set(companyId, {
            name: companyName,
            [year]: {},
          });
        }

        const companyStats = messageStats.get(companyId);
        if (!companyStats[year][month]) {
          companyStats[year][month] = {};
        }

        const amount =
          selectedAmountType === "send" ? 1 : msgCache.get(data.message?.id)?.pages || 1;

        const type = data.send_type || "electronique";

        updateStats(total[month], type, amount);
        updateStats(companyStats[year][month], type, amount);
      });

      const { chartData, tableData } = generateGlobalReports(messageStats, total, selectedMonth);

      // Cache the raw data for month switching
      cache.set(cacheKey, { messageStats, total, chartData, tableData });

      setStackedBarData(chartData);
      setTableStatsData(tableData);
    } catch (error) {
      console.error("Error fetching global stats:", error);
      message.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoadingTable(false);
    }
  }, [
    selectedYear,
    selectedMonth,
    selectedAmountType,
    companies,
    db,
    initializeYearData,
    updateStats,
    generateGlobalReports,
  ]);

  const fetchCompanyStats = useCallback(
    async (company) => {
      if (!company?.ref) {
        setLoadingTable(false);
        return;
      }

      const cacheKey = `company-${company.id}-${selectedYear}-${selectedAmountType}`;

      if (cache.has(cacheKey)) {
        const cachedData = cache.get(cacheKey);
        setStackedBarData(cachedData.chartData);
        setTableStatsData(cachedData.tableData);
        setLoadingTable(false);
        return;
      }

      const yearStart = new Date(`${selectedYear}-01-01`);
      const yearEnd = new Date(`${selectedYear}-12-31`);

      try {
        // Fetch messages if counting by pages
        const msgSnapshot =
          selectedAmountType === "pages"
            ? await db
                .collectionGroup("messages")
                .where("created_at", ">", yearStart)
                .where("created_at", "<", yearEnd)
                .get()
            : null;

        // Build message cache
        const msgCache = new Map();
        if (msgSnapshot) {
          msgSnapshot.forEach((doc) => {
            msgCache.set(doc.id, {
              pages: doc.data().pages || 1,
              id: doc.id,
            });
          });
        }

        const snapshot = await db
          .collectionGroup("recipients")
          .where("company", "==", company.ref)
          .where("created_at", ">=", yearStart)
          .where("created_at", "<=", yearEnd)
          .get();

        const messageStats = {};

        snapshot.forEach((doc) => {
          const data = doc.data();
          if (!data.created_at) return;

          const date = moment(data.created_at.toDate());
          const month = date.month();
          const year = date.year();

          if (!messageStats[year]) {
            messageStats[year] = {};
          }

          if (!messageStats[year][month]) {
            messageStats[year][month] = {};
          }

          const amount =
            selectedAmountType === "send" ? 1 : msgCache.get(data.message?.id)?.pages || 1;
          const type = data.send_type || "electronique";

          updateStats(messageStats[year][month], type, amount);
        });

        const { chartData, tableData } = generateCompanyReports(messageStats);

        cache.set(cacheKey, { chartData, tableData });

        setStackedBarData(chartData);
        setTableStatsData(tableData);
      } catch (error) {
        console.error("Error fetching company stats:", error);
        message.error("Erreur lors du chargement des statistiques");
      } finally {
        setLoadingTable(false);
      }
    },
    [selectedYear, selectedAmountType, db, updateStats, generateCompanyReports]
  );

  const fetchMessages = useCallback(
    async (company) => {
      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setLoadingTable(true);

      try {
        if (statsType === "global") {
          await fetchGlobalStats();
        } else {
          await fetchCompanyStats(company);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching messages:", error);
        }
      }
    },
    [statsType, fetchGlobalStats, fetchCompanyStats]
  );

  // Debounced fetch to avoid multiple rapid calls
  const debouncedFetchMessages = useMemo(() => _.debounce(fetchMessages, 300), [fetchMessages]);

  useEffect(() => {
    if (companies.length > 0) {
      debouncedFetchMessages(selectedCompany);
    }

    return () => {
      debouncedFetchMessages.cancel();
    };
  }, [
    selectedCompany,
    selectedYear,
    selectedMonth,
    selectedAmountType,
    companies,
    debouncedFetchMessages,
  ]);

  // Build chart - use ref to avoid dependency issues
  const chartRef = useRef(null);

  useEffect(() => {
    if (!stackedBarData.length) return;

    // Safely destroy existing chart
    if (chartRef.current) {
      try {
        chartRef.current.destroy();
      } catch (e) {
        // Silently handle destroy errors
      }
      chartRef.current = null;
    }

    // Check if container exists
    const container = document.getElementById("chart-stacked-bar");
    if (!container) return;

    const maxValue = _.maxBy(stackedBarData, "Nb")?.Nb || 0;

    const chart = new Chart({
      container: "chart-stacked-bar",
      autoFit: true,
      height: 300,
    });

    chart.data(stackedBarData);
    chart.scale("Nb", {
      alias: "Nombre d'envois",
      nice: true,
      tickCount: maxValue < 10 ? maxValue + 1 : 10,
    });

    chart.axis("Date", {
      tickLine: null,
    });

    chart.tooltip({
      showMarkers: false,
      shared: true,
    });

    chart.legend(false);
    chart.interaction("active-region");

    chart
      .interval()
      .adjust("stack")
      .position({ fields: ["Date", "Nb"] })
      .tooltip("Type*Nb*Total", (type, nb, total) => ({
        name: type,
        value: nb || total || 0,
      }))
      .color("Type", ["#40a9ff", "#1890ff", "#096dd9", "#0050b3"]);

    chart.render();
    chartRef.current = chart;
    setStackBarPlot(chart);

    // Cleanup function for this effect
    return () => {
      if (chartRef.current) {
        try {
          chartRef.current.destroy();
        } catch (e) {
          // Silently handle cleanup errors
        }
        chartRef.current = null;
      }
    };
  }, [stackedBarData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleExport = useCallback(() => {
    if (!exportedYears.length) {
      message.error("Veuillez sélectionner des dates à exporter");
      return;
    }

    if (!selectedFields.length) {
      message.error("Veuillez sélectionner des champs à exporter");
      return;
    }

    dispatch({ type: "SHOW_LOADER", payload: "Création du fichier CSV..." });
    setModalVisible(false);

    const sortedDates = [...exportedYears].sort();

    getUserStats({
      company: selectedCompany.id,
      fields: selectedFields,
      dates: sortedDates,
    })
      .then((response) => {
        const { data } = response;
        dispatch({ type: "HIDE_LOADER" });

        if (data?.error) {
          message.error(data.error);
        } else if (data) {
          window.location.href = `https://storage.googleapis.com/solidoc-proofs/${data}`;
        } else {
          message.error("Une erreur est survenue. Veuillez réessayer.");
        }
      })
      .catch((error) => {
        dispatch({ type: "HIDE_LOADER" });
        console.error("Export error:", error);
        message.error("Erreur lors de l'export");
      });
  }, [exportedYears, selectedFields, selectedCompany, dispatch, getUserStats]);

  const handleExportPrintingStats = useCallback(() => {
    dispatch({ type: "SHOW_LOADER", payload: "Génération du fichier Excel des impressions..." });
    setExportPrintingModalVisible(false);

    getMonthlyPrintingStats({
      year: exportPrintingYear,
      month: exportPrintingMonth,
    })
      .then((response) => {
        const { data } = response;
        dispatch({ type: "HIDE_LOADER" });

        if (data?.error) {
          message.error(data.error);
        } else if (data?.excel) {
          message.success(`Export généré avec ${data.count} lignes`);
          
          // Convert base64 to blob and trigger download
          const byteCharacters = atob(data.excel);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { 
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = data.filename || "export.xlsx";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          message.error("Une erreur est survenue. Veuillez réessayer.");
        }
      })
      .catch((error) => {
        dispatch({ type: "HIDE_LOADER" });
        console.error("Export printing stats error:", error);
        message.error("Erreur lors de l'export: " + (error.message || "Erreur inconnue"));
      });
  }, [exportPrintingYear, exportPrintingMonth, dispatch, getMonthlyPrintingStats]);

  return (
    <>
      <h1>Statistiques</h1>
      <h2>
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
          <div>
            Envois sur l&apos;année&nbsp;
            <Select placeholder="Type" onChange={setSelectedYear} value={selectedYear}>
              {yearOptions.map((year) => (
                <Select.Option value={year} key={year}>
                  {year}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div>
            Trier par&nbsp;
            <Select placeholder="Type" onChange={setSelectedAmountType} value={selectedAmountType}>
              <Select.Option value="send">Envois</Select.Option>
              <Select.Option value="pages">Pages</Select.Option>
            </Select>
          </div>
        </div>
      </h2>

      <Tabs
        activeKey={statsType}
        onChange={(value) => {
          if (value === "global") {
            setSelectedCompany(null);
          }
          setStatsType(value);
        }}
      >
        <TabPane tab="Globaux" key="global" />
        <TabPane tab="Individuels" key="individual" />
      </Tabs>

      {statsType === "individual" && (
        <Select
          style={{ marginBottom: 16, width: "100%" }}
          showSearch
          placeholder="Sélectionnez un compte"
          optionFilterProp="children"
          onChange={(value) => {
            setSelectedCompany(companies.find((c) => c.id === value));
          }}
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
          value={selectedCompany?.key}
        >
          {sortedCompanies.map((company) => (
            <Select.Option value={company.id} key={company.key}>
              {company.name}
            </Select.Option>
          ))}
        </Select>
      )}

      {(statsType === "global" || (statsType === "individual" && selectedCompany)) && (
        <div id="chart-stacked-bar" />
      )}

      {statsType === "global" && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
          <h3 style={{ margin: 0 }}>
            Envois du mois de&nbsp;
            <Select
              onChange={(value) => setSelectedMonth(parseInt(value))}
              value={selectedMonth.toString()}
            >
              {MONTHS.map((month, index) => (
                <Select.Option value={index.toString()} key={index}>
                  {month}
                </Select.Option>
              ))}
            </Select>
          </h3>
          <Button type="primary" onClick={() => setExportPrintingModalVisible(true)}>
            Export comptabilité
          </Button>
        </div>
      )}

      {((statsType === "individual" && selectedCompany) || statsType === "global") && (
        <>
          <Table
            style={{ marginTop: 16 }}
            columns={statsType === "global" ? globalColumns : individualColumns}
            loading={loadingTable}
            dataSource={tableStatsData}
            bordered
            size="middle"
            pagination={false}
          />

          {statsType === "individual" && (
            <Button
              type="default"
              style={{ float: "right", marginTop: 16 }}
              onClick={() => setModalVisible(true)}
            >
              Télécharger en CSV
            </Button>
          )}
        </>
      )}

      <Modal
        visible={modalVisible}
        title="Télécharger en CSV"
        okText="Télécharger"
        cancelText="Annuler"
        maskClosable={false}
        onCancel={() => setModalVisible(false)}
        onOk={handleExport}
      >
        <h4>Années à exporter</h4>
        <Select
          mode="multiple"
          size="middle"
          placeholder="Sélectionnez les années à exporter"
          value={exportedYears}
          style={{ width: "100%", marginBottom: 16 }}
          onChange={setExportedYears}
        >
          {yearOptions.map((year) => (
            <Select.Option value={year} key={year}>
              {year}
            </Select.Option>
          ))}
        </Select>

        <h4>Champs à exporter</h4>
        <Select
          mode="multiple"
          size="middle"
          placeholder="Sélectionnez les champs à exporter"
          value={selectedFields}
          style={{ width: "100%" }}
          onChange={setSelectedFields}
        >
          {EXPORT_FIELDS.map((field) => (
            <Select.Option value={field} key={field}>
              {field}
            </Select.Option>
          ))}
        </Select>
      </Modal>

      {/* Modal export comptabilité impressions */}
      <Modal
        visible={exportPrintingModalVisible}
        title="Export comptabilité - Statistiques d'impression"
        okText="Télécharger"
        cancelText="Annuler"
        maskClosable={false}
        onCancel={() => setExportPrintingModalVisible(false)}
        onOk={handleExportPrintingStats}
      >
        <p style={{ marginBottom: 16 }}>
          Exporter les statistiques d'impression mensuelles pour toutes les entreprises.
          <br />
          Ce fichier contient le détail des forfaits d'impression, pages supplémentaires et options recommandé.
        </p>

        <h4>Année</h4>
        <Select
          size="middle"
          value={exportPrintingYear}
          style={{ width: "100%", marginBottom: 16 }}
          onChange={setExportPrintingYear}
        >
          {yearOptions.map((year) => (
            <Select.Option value={year} key={year}>
              {year}
            </Select.Option>
          ))}
        </Select>

        <h4>Mois</h4>
        <Select
          size="middle"
          value={exportPrintingMonth}
          style={{ width: "100%" }}
          onChange={setExportPrintingMonth}
        >
          {MONTHS.map((month, index) => (
            <Select.Option value={index + 1} key={index + 1}>
              {month}
            </Select.Option>
          ))}
        </Select>
      </Modal>
    </>
  );
}
